import React from 'react';
import { Navigate, useLocation } from 'react-router';
import { useAuthContext } from '../contexts/AuthContext';
import { FullPageSpinner } from './FullPageSpinner';

interface ProtectedRouteProps {
    children: React.ReactNode;
    requireAuth?: boolean;
    redirectTo?: string;
}

/**
 * Protected route component that handles authentication-based routing
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
    children,
    requireAuth = true,
    redirectTo = '/auth/login',
}) => {
    const { isAuthenticated, loading } = useAuthContext();
    const location = useLocation();

    // Show loading spinner while checking authentication at app level
    if (loading) {
        return <FullPageSpinner message="Loading..." />;
    }

    // If authentication is required but user is not authenticated, redirect to login
    if (requireAuth && !isAuthenticated) {
        // Store the intended destination in sessionStorage as a fallback
        // This ensures redirect works even if location.state is lost (e.g., page refresh)
        const redirectPath = location.pathname + (location.search || '');
        if (location.pathname && location.pathname !== redirectTo) {
            sessionStorage.setItem('redirectAfterLogin', redirectPath);
            // Debug logging
            console.log('[ProtectedRoute] Storing redirect path:', redirectPath);
        }
        return <Navigate to={redirectTo} state={{ from: location }} replace />;
    }

    // If authentication is not required but user is authenticated, check for stored redirect path
    // Allow login and signup pages to be accessible even when authenticated
    if (!requireAuth && isAuthenticated) {
        // Check if there's a stored redirect path (from email links or direct URL navigation)
        const storedRedirect = sessionStorage.getItem('redirectAfterLogin');
        // Always allow login, signup, and register pages to be accessible (they handle their own logic)
        if (location.pathname === '/auth/login' || location.pathname === '/auth/signup' || location.pathname.startsWith('/auth/register')) {
            return <>{children}</>;
        }
        // Only redirect to home if there's no stored redirect path
        if (!storedRedirect) {
            return <Navigate to="/" replace />;
        }
        // If there's a stored redirect, let it through
        return <>{children}</>;
    }

    // Render children if authentication state matches requirements
    return <>{children}</>;
};
